import { Grid2X2, Search, UserRound, UserRoundPlus } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'

export interface AdminFilterOption {
  value: string
  label: string
}

interface AdminUsersToolbarProps {
  search: string
  searchPlaceholder: string
  onSearchChange: (value: string) => void
  role: string
  roleOptions: AdminFilterOption[]
  onRoleChange: (value: string) => void
  appAccess: string
  onAppAccessChange: (value: string) => void
  status: string
  statusOptions: AdminFilterOption[]
  onStatusChange: (value: string) => void
  onInvite: () => void
  inviteLabel?: string
  inviteButtonRef?: React.Ref<HTMLButtonElement>
}

const APP_ACCESS_OPTIONS: AdminFilterOption[] = [
  { value: 'all', label: 'All app access' },
  { value: 'both', label: 'Compass + Legacy OGSM' },
  { value: 'compass', label: 'Compass' },
  { value: 'ogsm', label: 'Legacy OGSM' },
  { value: 'none', label: 'No app access' },
]

export function AdminUsersToolbar({
  search,
  searchPlaceholder,
  onSearchChange,
  role,
  roleOptions,
  onRoleChange,
  appAccess,
  onAppAccessChange,
  status,
  statusOptions,
  onStatusChange,
  onInvite,
  inviteLabel = 'Invite user',
  inviteButtonRef,
}: AdminUsersToolbarProps) {
  return (
    <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-[minmax(260px,1fr)_170px_190px_170px_auto]">
      <div className="relative sm:col-span-2 xl:col-span-1">
        <Search
          className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-primary/70"
          aria-hidden="true"
        />
        <Input
          type="search"
          value={search}
          onChange={(event) => onSearchChange(event.target.value)}
          placeholder={searchPlaceholder}
          aria-label={searchPlaceholder}
          className="h-11 pl-10 text-sm"
        />
      </div>

      <FilterSelect
        value={role}
        onValueChange={onRoleChange}
        ariaLabel="Filter by role"
        icon={<UserRound className="size-4" aria-hidden="true" />}
        options={[{ value: 'all', label: 'All roles' }, ...roleOptions]}
      />
      <FilterSelect
        value={appAccess}
        onValueChange={onAppAccessChange}
        ariaLabel="Filter by app access"
        icon={<Grid2X2 className="size-4" aria-hidden="true" />}
        options={APP_ACCESS_OPTIONS}
      />
      <FilterSelect
        value={status}
        onValueChange={onStatusChange}
        ariaLabel="Filter by status"
        icon={
          <span
            className="size-3 rounded-full border-2 border-current"
            aria-hidden="true"
          />
        }
        options={statusOptions}
      />
      <Button
        ref={inviteButtonRef}
        variant="secondary"
        onClick={onInvite}
        className="h-11 gap-2 text-white hover:text-white sm:col-span-2 xl:col-span-1"
      >
        <UserRoundPlus className="size-4" aria-hidden="true" />
        {inviteLabel}
      </Button>
    </div>
  )
}

function FilterSelect({
  value,
  onValueChange,
  ariaLabel,
  icon,
  options,
}: {
  value: string
  onValueChange: (value: string) => void
  ariaLabel: string
  icon: React.ReactNode
  options: AdminFilterOption[]
}) {
  return (
    <Select value={value} onValueChange={onValueChange}>
      <SelectTrigger className="h-11 text-sm" aria-label={ariaLabel}>
        <div className="flex min-w-0 items-center gap-2">
          {icon}
          <SelectValue />
        </div>
      </SelectTrigger>
      <SelectContent>
        {options.map((option) => (
          <SelectItem key={option.value} value={option.value}>
            {option.label}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  )
}
